import { match } from 'ts-pattern';

import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';

import type { NormalizedTeam } from '../state/entities.store';
import type {
  PublicMarketFutureTeamChampion,
  PublicMarketGroup,
  PublicPredictionMarket,
} from '../services/interfaces/market.types';

export const GAMELINE_RULES_TABS = ['general', 'winBy', 'total'] as const;

export type GamelineRulesTab = (typeof GAMELINE_RULES_TABS)[number];

export type RulesEntry = {
  key: string;
  label: string;
  description?: string;
};

export type GamelineRules = {
  type: 'gameline';
  entriesByTab: Record<GamelineRulesTab, RulesEntry[]>;
};

export type FutureRules = {
  type: 'future';
  entries: RulesEntry[];
};

export type MarketGroupRules = GamelineRules | FutureRules;

type GamelineRulesInput = {
  markets: PublicPredictionMarket[];
  homeTeam: NormalizedTeam | undefined;
  awayTeam: NormalizedTeam | undefined;
};

type FutureRulesInput = {
  markets: PublicMarketFutureTeamChampion[];
  teamsById: Map<string, PublicTeam>;
};

export const getMarketRules = (market: PublicPredictionMarket | undefined): string | undefined =>
  market && 'rules' in market ? market.rules : undefined;

const findMoneylineForTeam = (
  markets: PublicPredictionMarket[],
  teamId: string
): PublicPredictionMarket | undefined =>
  markets.find(
    (market) =>
      market.type === 'moneyline' &&
      (('team_id' in market && market.team_id === teamId) ||
        ('yes_team_id' in market && market.yes_team_id === teamId))
  );

const toLineEntries = (market: PublicPredictionMarket | undefined): RulesEntry[] =>
  market ? [{ key: market.id, label: market.title, description: getMarketRules(market) }] : [];

export function getGamelineRules({
  markets,
  homeTeam,
  awayTeam,
}: GamelineRulesInput): GamelineRules {
  const teams = [awayTeam, homeTeam].filter((team): team is NormalizedTeam => !!team);

  return {
    type: 'gameline',
    entriesByTab: {
      general: teams.map((team) => ({
        key: team.id,
        label: team.name,
        description: getMarketRules(findMoneylineForTeam(markets, team.id)),
      })),
      winBy: toLineEntries(markets.find((market) => market.type === 'spread')),
      total: toLineEntries(markets.find((market) => market.type === 'total')),
    },
  };
}

export function getFutureRules({ markets, teamsById }: FutureRulesInput): FutureRules {
  return {
    type: 'future',
    entries: markets.map((market) => {
      const team = teamsById.get(market.team_id);

      return {
        key: market.id,
        label: team?.name ?? team?.alias ?? market.yes_line_display,
        description: market.rules,
      };
    }),
  };
}

export const getFutureMarkets = (
  marketGroup: PublicMarketGroup | undefined
): PublicMarketFutureTeamChampion[] =>
  marketGroup?.type === 'future_team_champion'
    ? marketGroup.markets.filter(
        (market): market is PublicMarketFutureTeamChampion => market.type === 'future_team_champion'
      )
    : [];

export const hasRules = (rules: MarketGroupRules | undefined): boolean =>
  !!rules &&
  match(rules)
    .with({ type: 'future' }, ({ entries }) => entries.length > 0)
    .with({ type: 'gameline' }, ({ entriesByTab }) =>
      GAMELINE_RULES_TABS.some((tab) => entriesByTab[tab].length > 0)
    )
    .exhaustive();
